import axios from "axios";
import {
  persistActiveMovementDraftBeforeLogout,
  preserveMovementDraftsOnSessionExpiry,
} from "../pages/Movements/Movement/utils/movementDraftStorage";

const BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:8080/api";

const api = axios.create({
  baseURL: BASE_URL,
  timeout: 20 * 60 * 1000,
});

// --- VARIÁVEIS DE CONTROLE ---
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) prom.reject(error);
    else prom.resolve(token);
  });
  failedQueue = [];
};

// --- INTERCEPTOR DE REQUEST ---
// Garante que pegamos sempre o token mais novo do storage
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    try {
      const application = JSON.parse(
        sessionStorage.getItem("selectedApplication") || "null",
      );
      const organization = JSON.parse(
        sessionStorage.getItem("selectedOrgan") || "null",
      );

      if (application?.id) {
        config.headers["Application-Id"] = application.id;
      }
      if (organization?.id) {
        config.headers["Organization-Id"] = organization.id;
      }
    } catch (error) {
      // A seleção de contexto não deve impedir chamadas à API se o storage estiver inválido.
      console.warn("Não foi possível carregar o contexto de auditoria.", error);
    }

    return config;
  },
  (error) => Promise.reject(error),
);

// --- INTERCEPTOR DE RESPONSE ---
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Se o erro for 401 e não for login ou retry
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url.includes("/login")
    ) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const oldToken = localStorage.getItem("token");

        // Chamada de refresh usando AXIOS PURO para não cair nos interceptors da instância 'api'
        const response = await axios.post(
          `${BASE_URL}/refresh`,
          {},
          {
            headers: { Authorization: `Bearer ${oldToken}` },
          },
        );

        const newToken = response.data.result;

        // 1. Salva no Storage
        localStorage.setItem("token", newToken);

        // 2. Atualiza o Header padrão da instância para chamadas futuras
        api.defaults.headers.common["Authorization"] = `Bearer ${newToken}`;

        // 3. Libera a fila
        processQueue(null, newToken);

        // 4. Retenta a chamada original com o token novo
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        persistActiveMovementDraftBeforeLogout();
        preserveMovementDraftsOnSessionExpiry();
        // Só redireciona se realmente o refresh falhar (expirou total)
        if (!originalRequest.url.includes("/refresh")) {
          window.location.href =
            "/login?message=Sessão expirada por inatividade.";
        }
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
    return Promise.reject(error);
  },
);

export default api;
